import { MdBarChart } from 'react-icons/md';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AgentApiAnalysisSalesSeries } from '@/types/serializers';
import InsightEmptyState from '../InsightEmptyState';
import SalesRegisterButton from './SalesRegisterButton';
import {
  AXIS_LABEL_COLOR,
  AXIS_TICK_COLOR,
  CHART_FOCUS_RESET,
  GRID_COLOR,
  formatPeriodLabel,
  formatYen,
  trendColorForIndex,
} from './salesChartUtils';

interface Props {
  title: string;
  series: AgentApiAnalysisSalesSeries;
  /** 凡例の位置。担当者別は上(top)、成約先は右(right)。 */
  legendPosition?: 'top' | 'right';
}

type TooltipPayloadItem = { dataKey?: string | number; value?: number | string; color?: string };
type TooltipProps = { active?: boolean; payload?: TooltipPayloadItem[]; label?: string };

function BarTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      {label && <span className="font-bold">{formatPeriodLabel(label)}</span>}
      {payload
        .filter((p) => (Number(p.value) || 0) > 0)
        .map((p) => (
          <div key={String(p.dataKey)} className="flex items-center gap-xxs">
            <span
              className="inline-block size-3 shrink-0 rounded-xs"
              style={{ backgroundColor: p.color }}
            />
            <span>{String(p.dataKey)}</span>
            <span className="font-bold">{formatYen(Number(p.value) || 0)}</span>
          </div>
        ))}
      <div className="flex items-center gap-md pt-xxs">
        <span>合計</span>
        <span className="font-bold">{formatYen(total)}</span>
      </div>
    </div>
  );
}

/**
 * bucket × グループの積み上げ棒グラフ（担当者別売上金額 / 成約先 売上金額で共通）。
 */
export default function SalesSeriesBarChart({ title, series, legendPosition = 'top' }: Props) {
  const hasData = series.series.length > 0;

  const data = series.points.map((p) =>
    series.series.reduce<Record<string, unknown>>(
      (acc, name) => {
        acc[name] = p.grouped_values[name] ?? 0;
        return acc;
      },
      { period: p.period }
    )
  );

  const chart = (
    <ResponsiveContainer width="100%" height={420}>
      <BarChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 12 }}>
        <CartesianGrid stroke={GRID_COLOR} strokeDasharray="4 4" vertical={false} />
        <XAxis
          dataKey="period"
          tickFormatter={formatPeriodLabel}
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          width={72}
          tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
          tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip cursor={{ fill: 'rgba(0,95,255,0.05)' }} content={<BarTooltip />} />
        {series.series.map((name, idx) => (
          <Bar
            key={name}
            dataKey={name}
            stackId="sales"
            fill={trendColorForIndex(idx)}
            barSize={40}
            radius={idx === series.series.length - 1 ? [4, 4, 0, 0] : undefined}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <div className={`w-full ${CHART_FOCUS_RESET}`}>
      <div className="flex items-start justify-between gap-md pb-sm">
        <h3 className="m-0 text-heading-xs text-main">{title}</h3>
        {!hasData && <SalesRegisterButton />}
      </div>

      {!hasData && (
        <InsightEmptyState
          icon={MdBarChart}
          description="データを登録すると、ここにグラフが表示されます"
        />
      )}

      {hasData && legendPosition === 'right' && (
        <div className="flex items-start gap-md">
          <div className="min-w-0 flex-1">{chart}</div>
          <ul className="m-0 flex max-h-[420px] w-[160px] shrink-0 list-none flex-col gap-xs overflow-y-auto p-0 text-body-sm text-main">
            {series.series.map((name, idx) => (
              <li key={name} className="flex min-w-0 items-center gap-xs">
                <span
                  className="inline-block size-[14px] shrink-0 rounded-xs"
                  style={{ backgroundColor: trendColorForIndex(idx) }}
                />
                <span className="truncate" title={name}>
                  {name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasData && legendPosition === 'top' && (
        <>
          <div className="mb-sm flex flex-wrap items-center gap-x-md gap-y-xxs text-body-sm text-main">
            {series.series.map((name, idx) => (
              <div key={name} className="flex items-center gap-xs">
                <span
                  className="inline-block size-[18px] shrink-0 rounded-sm"
                  style={{ backgroundColor: trendColorForIndex(idx) }}
                />
                {name}
              </div>
            ))}
          </div>
          {chart}
        </>
      )}
    </div>
  );
}
